import { useMemo, memo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTrackFilterUtilityClicked } from '@/features/shared/analytics/filters'
import { Checkbox } from '@shipt/design-system-checkbox'
import { type InputChangeEvent } from '@/features/shared/utils/dataFetching/types'
import { useOnChangeFilter } from '@/features/shop/components/ProductFilter/interactions/useOnChangeFilter'
import { useCurrentCategory } from '@/features/shop/components/ProductFilter/useCurrentCategory'
import { screenSizes } from '@shipt/design-system-themes'
import { type GeneralFacetItem } from '@/features/shop/services/Search/types'
import { getSelectedParams } from '@/features/shop/services/Search/utils'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { type SEARCH_FILTER_TYPE_FACET } from '@/features/shared/constants/global'
import { SearchIcon, NegativeCircleIcon } from '@shipt/design-system-icons'
import { shadow1 } from '@/features/shared/styles/effects'
import { Body } from '@shipt/design-system-typography'
import { ViewMoreButton } from '@/features/shop/components/ProductFilter/ViewMoreButton'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { Box } from '@shipt/design-system-layouts'
import { useValidSearchParams } from '@/features/shared/hooks/search'

type Props = {
  items: GeneralFacetItem[]
  name: SEARCH_FILTER_TYPE_FACET
  hideSearchInput?: boolean
}
export const PillFilterSection = memo(({ items = [], name, hideSearchInput }: Props) => {
  const currentCategory = useCurrentCategory()
  const { onChangeFilter } = useOnChangeFilter()
  const trackFilterUtilityClicked = useTrackFilterUtilityClicked()
  const searchParams = useValidSearchParams()
  const query = searchParams[name]
  const filterItems = useMemo(() => {
    const selectItems = getSelectedParams(query ?? '')
    const unselectedItems = items.flatMap((item) =>
      selectItems.includes(item.name) ? [] : [item.name],
    )
    return [...selectItems, ...unselectedItems]
  }, [items, query])

  const [searchText, setSearchText] = useState('')
  const [isShowingMore, setIsShowingMore] = useState(false)

  const initialShowLimit = 6
  const showLimit = isShowingMore ? items.length : initialShowLimit

  const textboxRef = useRef<HTMLInputElement>(null)
  const itemsToDisplay = useMemo(() => {
    return searchText
      ? filterItems.filter((item) => {
          const lc = item.toLowerCase()
          const filter = searchText.toLowerCase()
          return lc.includes(filter)
        })
      : filterItems.slice(0, showLimit)
  }, [filterItems, searchText, showLimit])
  const showViewButton = filterItems.length > initialShowLimit && !searchText

  const { [name]: itemsFromQueryParams = [] } = searchParams

  function handleItemChange(e: InputChangeEvent, item: string, filterIdx: number) {
    const isChecked = e.target.checked
    const itemsToUpdate = isChecked
      ? [...itemsFromQueryParams, item]
      : itemsFromQueryParams.filter((i: string) => i !== item)

    const params = { [name]: itemsToUpdate }

    onChangeFilter(params)

    trackFilterUtilityClicked({
      params,
      lineage: currentCategory?.lineage,
      filter_active: itemsToUpdate.length > 0,
      filter_value: name,
      list_index: filterIdx,
      utility_type: 'filter',
      display_format: DISPLAY_FORMAT.VERTICAL,
      display_sublocation: `${name}_${DISPLAY_FORMAT.VERTICAL}`,
    })
  }

  const trimmedName = useMemo(() => {
    if (name.endsWith('s')) {
      return name.slice(0, -1)
    }
    return name
  }, [name])

  return (
    <>
      {!hideSearchInput && (
        <InputWrapper onClick={() => textboxRef.current?.focus()}>
          <FilterSearchIcon size="sm" />
          <Body>
            <FilterInput
              type="text"
              ref={textboxRef}
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
              aria-label={`Enter a ${trimmedName}`}
              placeholder={`Search ${name}`}
            />
          </Body>
          {!!searchText && (
            <ResetButton aria-label="reset" onClick={() => setSearchText('')}>
              <NegativeCircleIcon />
            </ResetButton>
          )}
        </InputWrapper>
      )}
      {itemsToDisplay.map((item, i) => (
        <Box key={item} padding={{ base: ['sm', 0], md: [0] }} marginBottom={{ md: 'sm' }}>
          <Checkbox
            name="filter options"
            checked={itemsFromQueryParams.includes(item)}
            onChange={(e) => handleItemChange(e, item, i)}
            label={item}
          />
        </Box>
      ))}
      {!!showViewButton && (
        <ViewMoreButton
          isShowingMore={isShowingMore}
          onClick={() => setIsShowingMore((prev) => !prev)}
        />
      )}
    </>
  )
})

const FilterSearchIcon = styled(SearchIcon)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
  color: ${({ theme }) => theme.gray600};
`

const InputWrapper = styled.div`
  &:hover {
    box-shadow: 0 0 8px rgb(0 0 0 / 10%);
  }

  ${shadow1};
  display: none;
  padding: 8px;
  height: 2.5rem;
  align-items: center;
  border-radius: 8px;
  border: 2px solid;
  margin-bottom: 1.5rem;
  border-color: transparent;

  &:focus-within {
    border-color: ${({ theme }) => theme.plum};
  }

  @media ${screenSizes.tablet} {
    margin-bottom: 1rem;
    display: flex;
  }
`
const FilterInput = styled.input`
  font: inherit;
  border: none;
  width: 10rem;
  max-width: 10rem;
  color: ${({ theme }) => theme.gray600};
  caret-color: ${({ theme }) => theme.purple500};

  &:focus::placeholder {
    color: ${({ theme }) => theme.gray400};
  }
`
const ResetButton = styled(TransparentButton)`
  display: flex;
  align-items: center;
  padding-left: 4px;

  svg {
    border-radius: 100%;
    padding: 4px;
    width: 1rem;
    height: 1rem;
    color: ${({ theme }) => theme.gray600};

    &:hover {
      background-color: ${({ theme }) => theme.gray100};
    }
  }
`
